import type { MailLinkDto } from "../../services/mailLinkService";
import type { Email } from "../../services/graphMailService";

/**
 * Reine, testbare Helfer der Mail-Kontakt-Sidebar (SP6, Reiter „Schriftwechsel").
 * Die UI (MailContactSidebar.tsx) hält nur State + Fetch; die Logik zum Bauen
 * der Graph-KQL und zum Zusammenführen der postfachübergreifenden Treffer lebt
 * hier, damit sie ohne DOM getestet werden kann.
 */

/** Ein Schriftwechsel-Treffer trägt sein Quell-Postfach (Suche ist global). */
export type SidebarThreadHit = Email & { srcMailbox: string; link?: MailLinkDto };

/**
 * C1 — Effektive Adressmenge der Kontakthistorie (Schriftwechsel + Termine).
 * Führt beliebig viele Adresslisten zusammen: die gespeicherten Kontaktadressen
 * (falls die Mail einem KMVS-Kontakt zugeordnet ist) UND die Adresse(n) der
 * geöffneten Mail selbst — so funktioniert die Historie „von/an diese E-Mail-
 * Adresse" auch ganz OHNE Zuweisung. Normalisiert (trim/lowercase), verwirft
 * Leeres und dedupliziert unter Beibehaltung der Reihenfolge.
 */
export function effectiveHistoryAddresses(
    ...lists: (readonly (string | null | undefined)[] | null | undefined)[]
): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const list of lists) {
        for (const raw of list ?? []) {
            const a = String(raw ?? "").trim().toLowerCase();
            if (a.length === 0 || seen.has(a)) continue;
            seen.add(a);
            out.push(a);
        }
    }
    return out;
}

/**
 * KQL für die postfachübergreifende `participants:`-Suche: eine ODER-Kette über
 * alle (bereinigten, distinct, kleingeschriebenen) Kontakt-Adressen. Leere/
 * doppelte Adressen fallen raus; ohne Adresse ergibt sich ein leerer String
 * (der Aufrufer sucht dann gar nicht).
 */
export function buildParticipantsQuery(addresses: string[]): string {
    const clean = Array.from(
        new Set(
            (addresses ?? [])
                .map((a) => String(a ?? "").trim().toLowerCase())
                .filter((a) => a.length > 0),
        ),
    );
    return clean.map((a) => `participants:"${a}"`).join(" OR ");
}

/**
 * Treffer aller Postfächer zusammenführen: dedupliziert über (Postfach + id)
 * und sortiert neueste zuerst. Defensiv gegen fehlende `date` (rehydrierte
 * Alt-Objekte) — invalide Daten sortieren ans Ende.
 */
export function mergeThreadHits(hits: SidebarThreadHit[]): SidebarThreadHit[] {
    const seen = new Map<string, SidebarThreadHit>();
    const out: SidebarThreadHit[] = [];
    for (const h of hits ?? []) {
        const key = `${h.srcMailbox}:${h.id}`;
        const existing = seen.get(key);
        if (existing) {
            // Den reicheren Graph-Treffer behalten, aber seine stabile Verknüpfung ergänzen.
            existing.link ??= h.link;
            continue;
        }
        const copy = { ...h };
        seen.set(key, copy);
        out.push(copy);
    }
    const ts = (d: Date): number => {
        const t = d instanceof Date ? d.getTime() : new Date(d as unknown as string).getTime();
        return Number.isFinite(t) ? t : -Infinity;
    };
    return out.sort((a, b) => ts(b.date) - ts(a.date));
}

/**
 * J2 — die Adressmenge des Schriftwechsel-Reiters, wenn die Mail einem Kontakt
 * ZUGEWIESEN ist.
 *
 * Bewusst KEINE Vereinigung mehr mit der Absenderadresse (anders als
 * {@link effectiveHistoryAddresses}, das für Termine und den zuweisungsfreien
 * Fall bleibt): Nach „nur diese MAIL zuweisen" (B3, der @courte.de-Fall)
 * gehört die Absenderadresse dem Kontakt NICHT — ihren Verlauf unter seinem
 * Namen zu zeigen wäre falsch. PO: „wenn es dem Kontakt zugewiesen ist, dann
 * immer den Schriftwechsel von diesem Kontakt anzeigen."
 *
 * Hat der Kontakt gar keine hinterlegte Adresse, fällt die Anzeige sichtbar auf
 * die Mailadresse zurück (`rueckfall = true`) — lieber ein erklärter Verlauf
 * als eine leere Liste.
 */
export function historieAdressen(
    kontaktAdressen: string[] | null | undefined,
    mailAdressen: string[] | undefined,
    hatKontakt: boolean,
): { adressen: string[]; rueckfall: boolean } {
    if (!hatKontakt) {
        return { adressen: effectiveHistoryAddresses(mailAdressen), rueckfall: false };
    }
    const eigene = effectiveHistoryAddresses(kontaktAdressen);
    if (eigene.length > 0) return { adressen: eigene, rueckfall: false };
    return { adressen: effectiveHistoryAddresses(mailAdressen), rueckfall: true };
}
